import { useCallback, useState } from 'react';
import { Button, Text } from 'rizzui';
import { IoArrowBackOutline } from 'react-icons/io5';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { Loader } from '../../../components/loader/SpinnerLogo';
import { useFiltersPR } from '../../../components/providers/FiltersProvider';
import CustomTable from '../../../components/table/CustomTable';
import { FilterDrawerView } from '../../../components/table/components/TableFilter';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import Page from '../../../layout/Page/Page';
import { menuRoutes } from '../../../router/menu';
import { NetworkService } from '../../../services/network/networkService';
import { fixNumber } from '../../../utils/formatNumber';
import NetworkSettlementsFilters from './NetworkSettlementsFilters';

const columnOrder = [
    'contract',
    'student',
    'type',
    'contractAmount',
    'commissionPercentage',
    'totalCommission',
    'adjustments',
    'state',
];

const NetworkSettlementContractsList = () => {
    const service = new NetworkService();
    const { handleErrors } = useHandleErrors();
    const { filters, updateFilters, updateFilterOrder, updatePage, updatePageSize, resetFilters } = useFiltersPR();
    const { commercialNetworkId = '' } = useParams();
    const navigate = useNavigate();
    const location = useLocation();

    const [openFilters, setOpenFilters] = useState<boolean>(false);
    const [isExporting, setIsExporting] = useState<boolean>(false);

    const [data, loading] = useFetch(useCallback(async () => {
        const requestFilters = {
            ...filters,
            filter_filters: {
                ...(filters.filter_filters ?? {}),
                commercial_network: [commercialNetworkId],
            },
        };
        const response = await service.listNetworkSettlementContracts(requestFilters);
        return response.getResponseData();
    }, [filters, commercialNetworkId]));

    const orderedData = () => data?.data?.map((row: any) => {
        const orderedRow: any = { id: row.id };
        columnOrder.forEach((key) => {
            orderedRow[key] = row[key];
        });
        return orderedRow;
    });

    const handleExport = async () => {
        setIsExporting(true);
        try {
            const exportFilters = {
                ...filters,
                filter_filters: {
                    ...(filters.filter_filters ?? {}),
                    commercial_network: [commercialNetworkId],
                },
            };

            const response = await service.exportNetworkSettlements(exportFilters);
            if (!response) {
                toast.error('No se pudo exportar el fichero de liquidaciones');
                return;
            }

            const fileData = response.getResponseData();
            const blob = new Blob([fileData]);
            const url = window.URL.createObjectURL(blob);
            const selectedMonth = Number(filters.filter_filters?.month ?? (new Date().getMonth() + 1));
            const selectedYear = Number(filters.filter_filters?.year ?? new Date().getFullYear());

            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `liquidaciones_red_${selectedYear}_${String(selectedMonth).padStart(2, '0')}_${commercialNetworkId}.xlsx`);
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (requestError: any) {
            const responseData = requestError?.response?.data;
            if (responseData?.message) {
                toast.error(responseData.message);
            } else {
                handleErrors(responseData ?? requestError);
            }
        } finally {
            setIsExporting(false);
        }
    };

    const networkNameFromState = (location.state as any)?.commercialNetworkName;
    const networkName = networkNameFromState || data?.data?.[0]?.commercialNetworkName || 'Red Comercial';

    return (
        <Page container='fluid'>
            <div className="mb-4 flex items-center gap-3">
                <Button
                    variant="flat"
                    size="sm"
                    onClick={() => navigate(`${menuRoutes.networks.path}${menuRoutes.networks.settlements}`)}
                >
                    <IoArrowBackOutline className="me-2 h-4 w-4" />
                    Volver
                </Button>
                <Text className="font-semibold">{networkName}</Text>
            </div>

            {data && (
                <CustomTable
                    largeTable
                    id={'network-settlement-contracts-table'}
                    columnOrder={columnOrder}
                    data={orderedData()}
                    isLoading={loading}
                    overrideColumns={[
                        {
                            key: 'contractAmount',
                            render: (row: any) => <Text>{fixNumber(Number(row.row.original.contractAmount), 2)} €</Text>
                        },
                        {
                            key: 'commissionPercentage',
                            render: (row: any) => <Text>{fixNumber(Number(row.row.original.commissionPercentage), 2)} %</Text>
                        },
                        {
                            key: 'totalCommission',
                            render: (row: any) => <Text>{fixNumber(Number(row.row.original.totalCommission), 2)} €</Text>
                        },
                        {
                            key: 'adjustments',
                            render: (row: any) => (
                                <Text className={Number(row.row.original.adjustments) < 0 ? 'text-red-600 font-medium' : ''}>
                                    {fixNumber(Number(row.row.original.adjustments), 2)} €
                                </Text>
                            )
                        },
                    ]}
                    filters={filters}
                    updateFilters={updateFilters}
                    updateFilterOrder={updateFilterOrder}
                    defaultOrder={filters.filter_order || undefined}
                    paginationData={{
                        pageSize: filters.limit,
                        currentPage: filters.page,
                        pageCount: data?.lastPage || 1,
                        totalCount: data?.totalRegisters || 0,
                        handlePagination: updatePage,
                        handlePerPage: updatePageSize,
                    }}
                    toggleFilters={() => setOpenFilters(!openFilters)}
                >
                    <Button
                        variant={'solid'}
                        color={'secondary'}
                        size={'sm'}
                        onClick={handleExport}
                        className='p-3 text-nowrap'
                        disabled={isExporting}
                        isLoading={isExporting}
                    >
                        Exportar Excel
                    </Button>
                </CustomTable>
            )}

            {!data && loading && (<Loader height='40vh' />)}

            <FilterDrawerView isOpen={openFilters} setOpenDrawer={setOpenFilters} drawerTitle={'Filtros Liquidaciones de Red'}>
                <NetworkSettlementsFilters
                    filters={filters}
                    updateFilters={updateFilters}
                    resetFilters={resetFilters}
                    hideCommercialNetwork
                />
            </FilterDrawerView>
        </Page>
    );
};

export default NetworkSettlementContractsList;
